import {
  leadsContactsListOptions,
  opportunitiesContactsListOptions,
  ReturnContactReadable
} from '@crm/api'
import { PhoneNumbers } from '@crm/components'
import { OpportunityContext } from '@crm/contexts/OpportunityContext'
import { getUserFullName } from '@crm/utils'
import { TableActions, TableContainer } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Flex, MenuProps, TableColumnsType, Typography } from 'antd'
import { Edit2, Trash } from 'iconsax-react'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AddEditLead } from './AddEditLead'
import { DeleteLead } from './DeleteLead'
import { LeadsHeader } from './LeadsHeader'
import { ActionsType } from './types'

const { Link } = Typography

type ModalType = 'edit' | 'delete'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  edit: AddEditLead,
  delete: DeleteLead
}

export const LeadsCol = () => {
  const { t } = useLocales()
  const { id, opportunityId } = useParams()
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    lead: ReturnContactReadable | null
  }>({ lead: null, type: null })
  const { type } = useContext(OpportunityContext)

  const { params } = useQueryUpdate()

  const { data: leadData, isLoading: isLeadLoading } = useQuery({
    ...leadsContactsListOptions({
      path: { lead_id: String(id) },
      query: { ...params }
    }),
    enabled: !!id && type === 'lead'
  })

  const { data: opportunityData, isLoading: isOpportunityLoading } = useQuery({
    ...opportunitiesContactsListOptions({
      path: { opportunity_id: String(opportunityId) },
      query: { ...params }
    }),
    enabled: !!opportunityId && type === 'opportunity'
  })

  const data = leadData || opportunityData
  const isLoading = isLeadLoading || isOpportunityLoading

  const actionItems = (lead: ReturnContactReadable): MenuProps['items'] => [
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ lead, type: 'edit' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ lead, type: 'delete' })
    }
  ]

  const columns: TableColumnsType<ReturnContactReadable> = [
    {
      title: t('generic.name'),
      dataIndex: 'name',
      key: 'name',
      render: (_, contact) => getUserFullName(contact)
    },
    {
      title: t('contacts.email'),
      dataIndex: 'email',
      key: 'email',
      render: (email) =>
        email ? (
          <Link href={`mailto:${email}`} target="_blank" rel="noreferrer">
            {email}
          </Link>
        ) : (
          '-'
        )
    },
    {
      title: t('contacts.phone_number'),
      dataIndex: 'phone_number',
      key: 'phone_numbers ',
      render: (_, record) => (
        <PhoneNumbers
          onClick={(e) => e.stopPropagation()}
          phoneNumbers={record?.phone_numbers}
        />
      )
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, contact) => <TableActions options={actionItems(contact)} />
    }
  ]

  const actionComponent = useMemo(() => {
    const Component = modalState.type ? modalTypes[modalState.type] : null

    return Component && modalState.lead ? (
      <Component
        open
        lead={modalState.lead}
        onCancel={() => setModalState({ lead: null, type: null })}
      />
    ) : null
  }, [modalState])

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        data={data}
        loading={isLoading}
        rightChildren={<LeadsHeader />}
      />

      {actionComponent}
    </Flex>
  )
}
